// apps/api/src/qxcircle/qxcircle-mute.ts
// Description: QXCircle mute author — hide from home feed only (Slice 20).
// Owner: Masa Bayu
// Date: 2026-08-24

import { BadRequestException, NotFoundException } from "./_nestjs-common";
import { findProfileSeed } from "./qxcircle-profile-seeds";
import { normalizeQxCircleAuthorHandle } from "./qxcircle-service-helpers";
import type {
  QxCircleFeedPost,
  QxCircleMuteResult,
  QxCircleMutesResponse,
} from "./qxcircle.types";

export function filterQxCircleFeedMutes(
  posts: QxCircleFeedPost[],
  mutedHandles: Set<string>,
): QxCircleFeedPost[] {
  return posts.filter((post) => !mutedHandles.has(post.authorHandle));
}

export function buildQxCircleMutesList(
  posts: QxCircleFeedPost[],
  mutedHandles: Set<string>,
): QxCircleMutesResponse {
  const items = [...mutedHandles]
    .map((authorHandle) => {
      const post = posts.find((p) => p.authorHandle === authorHandle);
      const seed = findProfileSeed(authorHandle);
      return {
        authorHandle,
        authorName: post?.authorName ?? seed?.authorName ?? authorHandle,
      };
    })
    .sort((a, b) => a.authorHandle.localeCompare(b.authorHandle));
  return {
    world: "qxcircle",
    slice: "mute",
    count: items.length,
    items,
  };
}

export function toggleQxCircleMuteAuthor(
  handleRaw: string,
  posts: QxCircleFeedPost[],
  localMutes: Set<string>,
  viewerHandle: string | null = null,
): QxCircleMuteResult {
  const authorHandle = normalizeQxCircleAuthorHandle(handleRaw);
  if (viewerHandle && authorHandle === viewerHandle) {
    throw new BadRequestException("Cannot mute yourself");
  }
  const authorPost = posts.find((p) => p.authorHandle === authorHandle);
  if (!authorPost) {
    throw new NotFoundException(`Author not found: ${authorHandle}`);
  }
  if (localMutes.has(authorHandle)) {
    localMutes.delete(authorHandle);
  } else {
    localMutes.add(authorHandle);
  }
  const mutedByLocal = localMutes.has(authorHandle);
  return {
    authorHandle,
    authorName: authorPost.authorName,
    mutedByLocal,
    note: mutedByLocal
      ? "Author muted — hidden from home feed (profile still visible)"
      : "Author unmuted",
  };
}
